<template>
  <div class="absolute">
    <vetster-slide-over
      ref="partnerSlideOver"
      :size="isLargeScreen ? 'full' : 'md'"
      @close="closePartnerDetails"
    >
      <template #header>
        <vetster-slide-over-common-heading
          :show-back="isRecommended"
          :show-border="!isLargeScreen"
          :show-close="!isRecommended"
          @close="close"
          @back="close"
        >
          <template>
            <vetster-recommend-vet-stats
              v-if="isRecommended"
              :top-rated-text="getTopRatedText"
              :is-prescription-available="hasVCPR && isVcprSearch"
              :review-count="partner.rating_count"
              class="lg:hidden !mt-5 !mb-7"
            />
            <vetster-partner-profile
              v-if="!isLargeScreen"
              :is-in-state="isInState"
              :is-vcpr-enabled="hasVCPR"
              :is-vcpr-search="isVcprSearch"
              :partner="partner"
              :show-reviews-button="showAllReviewsButton"
              @toggle-reviews.stop="toggleReviews"
            />
            <vetster-partner-badges
              v-if="!isLargeScreen"
              :is-in-state="isInState"
              :is-vcpr-enabled="hasVCPR"
              :is-vcpr-search="isVcprSearch"
              :partner="partner"
            />
          </template>
        </vetster-slide-over-common-heading>
      </template>

      <template #content>
        <div
          ref="scrollContainer"
          class="flex-1 overflow-y-auto flex flex-col"
          @scroll="handleScroll"
        >
          <div class="lg:px-standard">
            <vetster-recommend-vet-stats
              v-if="isRecommended"
              :top-rated-text="getTopRatedText"
              :is-prescription-available="hasVCPR && isVcprSearch"
              :review-count="partner.rating_count"
              class="col-span-6 !my-5 w-full max-w-screen-xl mx-auto hidden lg:block"
            />
          </div>

          <div class="flex flex-col w-auto lg:w-full lg:flex-row lg:gap-24 lg:px-standard lg:max-w-screen-xl lg:mx-auto">
            <vetster-partner-details-skeleton v-if="isPartnerLoading && isLargeScreen" />

            <!-- Px detail standalone sections -->
            <component
              :is="selectedComponent"
              v-else-if="partnerDetailsComponent && isLargeScreen"
              v-bind="selectedComponentBindings"
              :ref="selectedComponent.name"
              class="py-6"
              @previous="closePartnerDetails"
            />

            <!-- Px details -->
            <vetster-slot-wrapper
              v-else
              :slots="getPartnerDetailCards"
              class="[&_>_div:first-child_>_div]:border-t-0 lg:min-h-screen"
            >
              <!-- Book -->
              <template #book>
                <vetster-partner-detail-card
                  v-if="!isLargeScreen"
                  :title="$t('search.book-appointment')"
                >
                  <p class="mt-1 text-neutral-500 mb-4">
                    {{ $t('search.choose-the-next-available') }}
                  </p>
                  <!-- Timeslots -->
                  <div
                    v-if="isPartnerLoading || !pageReady"
                    class="flex flex-col"
                  >
                    <div class="overflow-x-auto flex space-x-2 scrollbar-none">
                      <div
                        v-for="index in 3"
                        :key="index"
                        class="w-40 rounded-lg h-20 shrink-0 bg-neutral-200 animate-pulse"
                      />
                    </div>
                    <div class="mt-4 w-52 h-6 rounded-md shrink-0 bg-neutral-200 animate-pulse" />
                  </div>
                  <template v-else>
                    <vetster-partner-timeslots
                      :timezone="timezone"
                      :partner-timeslots="partnerTimeslots"
                      :previous-timeslot="previousTimeslot"
                    />
                    <vetster-button
                      type="button"
                      variant="textLink"
                      size="tight"
                      class="mt-4 *:text-neutral-900"
                      @click="toggleQuotePanel"
                    >
                      <span class="inline-flex font-normal text-sm underline underline-offset-2">
                        {{ $t('search.show-more-dates-and-time') }}
                      </span>
                    </vetster-button>
                  </template>
                </vetster-partner-detail-card>
              </template>

              <!-- Booked before -->
              <template #booked-before>
                <vetster-partner-detail-card
                  v-if="!isLargeScreen"
                  inner-wrapper-class="pb-7"
                >
                  <div class="space-y-6">
                    <!-- Booked this vet before card -->
                    <div
                      v-if="lastSeenByVet"
                      class="bg-white shadow-lg flex-1 p-4 rounded-lg space-y-2 border border-neutral-200"
                    >
                      <div class="flex space-x-2">
                        <fa
                          :icon="['fal', 'heart']"
                          class="fa-fw text-2xl text-bubblegum-500"
                        />
                        <p class="font-semibold text-neutral-900 flex-1">
                          {{ $t('search.youve-booked-this-vet-before') }}
                        </p>
                        <fa
                          class="text-bubblegum-500 text-2xl ml-auto"
                          :icon="['fal', 'check']"
                        />
                      </div>
                      <p class="text-neutral-500 text-sm">
                        {{ $t('search.your-last-visit-was-date', { vet: fullName, date: lastSeenAtDate }) }}
                      </p>
                    </div>
                    <!-- Species treated top rated card -->
                    <div class="bg-white shadow-lg flex-1 p-4 rounded-lg space-y-2 border border-neutral-200">
                      <div class="flex flex-1 space-x-2">
                        <fa
                          :icon="petIcon(searchSpecies)"
                          class="fa-fw text-2xl text-bubblegum-500"
                        />
                        <p class="font-semibold text-neutral-900 flex-1">
                          {{ getTopRatedText }}
                        </p>
                        <fa
                          class="text-bubblegum-500 text-2xl ml-auto"
                          :icon="['fal', 'check']"
                        />
                      </div>
                    </div>
                  </div>
                </vetster-partner-detail-card>
              </template>

              <!-- Px details -->
              <template #px-details>
                <vetster-partner-detail-card>
                  <vetster-partner-profile
                    v-if="isLargeScreen"
                    :is-in-state="isInState"
                    :is-vcpr-enabled="hasVCPR"
                    :is-vcpr-search="isVcprSearch"
                    :partner="partner"
                    show-badges
                    :show-reviews-button="showAllReviewsButton"
                    @toggle-reviews.stop="toggleReviews"
                  />
                  <p class="lg:mt-6">
                    {{ getDescription(partner.description) }}
                  </p>
                  <vetster-button
                    v-if="descriptionExceedsLimit"
                    type="button"
                    variant="textLink"
                    size="tight"
                    class="mt-4 space-x-1 *:text-neutral-900"
                    @click.stop="openAboutSlideOver"
                  >
                    <span class="inline-flex font-normal text-base underline underline-offset-2">
                      {{ $t('search.show-full-profile') }}
                    </span>
                    <fa
                      :icon="['fal', 'angle-right']"
                      class="fa-fw"
                    />
                  </vetster-button>
                </vetster-partner-detail-card>
              </template>

              <!-- Species treated -->
              <template #species-treated>
                <vetster-partner-detail-card v-if="hasSpeciesTreated">
                  <vetster-slide-navigation-wrapper
                    :heading="$t('search.species-treated')"
                    container-classes="-ml-4 sm:-ml-6 lg:ml-0 -mr-4 sm:-mr-6 lg:mr-0"
                  >
                    <vetster-species-treated
                      :species-treated="speciesTreated"
                      class="mt-4 pb-7"
                    />
                  </vetster-slide-navigation-wrapper>
                </vetster-partner-detail-card>
              </template>

              <!-- Areas of interest -->
              <template #areas-of-interest>
                <vetster-partner-detail-card
                  v-if="hasAreasOfInterest"
                  :title="$t('search.areas-of-interest')"
                >
                  <div class="space-y-4">
                    <vetster-areas-of-interest
                      :areas-of-interest="areasOfInterest"
                      :limit="areasOfInterestLimit"
                    />
                    <vetster-button
                      v-if="showAreasOfInterestButton"
                      type="button"
                      variant="white"
                      class="flex w-full lg:w-auto"
                      @click.stop="openAreasOfInterestSlideOver"
                    >
                      {{ $t('search.show-all-areas-of-interest') }}
                    </vetster-button>
                  </div>
                </vetster-partner-detail-card>
              </template>

              <!-- Licenses -->
              <template #licenses>
                <vetster-partner-detail-card :title="$t('search.licenses')">
                  <ul>
                    <li
                      v-for="(license, index) in partnerDetails.licenses"
                      :key="`license-${index}`"
                      class="flex items-center mt-2 *:text-neutral-900 space-x-4"
                    >
                      <fa
                        :icon="['fal', 'file-certificate']"
                        class="text-base"
                      />
                      <p>{{ license.jurisdiction.name }} ({{ license.jurisdiction.formatted_name }})</p>
                    </li>
                  </ul>
                </vetster-partner-detail-card>
              </template>

              <!-- Reviews -->
              <template #reviews>
                <vetster-partner-detail-card
                  v-if="hasRatings"
                  inner-wrapper-class="pb-0"
                >
                  <template #title>
                    <vetster-reviews-count :partner="partner" />
                  </template>
                  <vetster-reviews
                    :partner="partner"
                    :per-page="reviewsLimit"
                    :default-reviews="reviews"
                    class="mt-4 pb-7"
                    @review-clicked.stop="toggleReviews"
                  />

                  <div class="pr-4 sm:pr-6">
                    <vetster-button
                      v-if="showAllReviewsButton"
                      type="button"
                      variant="white"
                      class="flex w-full mb-6 lg:w-auto"
                      @click.stop="toggleReviews"
                    >
                      {{ $t('search.show-all-review-count', { count: partner.rating_count }) }}
                    </vetster-button>
                  </div>
                </vetster-partner-detail-card>
              </template>

              <template #recommend-a-vet>
                <vetster-partner-detail-card>
                  <button
                    type="button"
                    :aria-label="$t('search.show-me-other-vets')"
                    class="flex items-center space-x-2.5 text-neutral-900"
                    @click="$emit('navigate-to-guided-search')"
                  >
                    <fa :icon="['fal', 'user-magnifying-glass']" />
                    <span class="underline underline-offset-2">
                      {{ $t('search.show-me-other-vets') }}
                    </span>
                  </button>
                </vetster-partner-detail-card>
              </template>
            </vetster-slot-wrapper>

            <!-- Timeslot picker -->
            <div
              v-if="isLargeScreen"
              class="pt-6 lg:min-h-screen"
            >
              <vetster-partner-quote-panel
                :partner="partner"
                :timezone="timezone"
                :search-params="searchParams"
                class="sticky top-10 mx-auto"
                @checkout="checkout"
              />
            </div>
          </div>
        </div>
      </template>

      <!-- Quote total & Book now button -->
      <template #footer>
        <transition
          v-if="!isPartnerLoading && !isLargeScreen"
          name="fade-fast"
          appear
          mode="out-in"
        >
          <div>
            <vetster-quote-total-banner v-if="!isPartnerLoading && showPaymentOption && hasAvailability" />
            <vetster-quote-total
              @toggle-quote-panel="toggleQuotePanel"
              @checkout="checkout"
            />
          </div>
        </transition>
      </template>
    </vetster-slide-over>

    <div v-if="!isLargeScreen">
      <vetster-partner-about
        ref="aboutSlideOver"
        :description="partner.description"
      />

      <vetster-partner-areas-of-interest
        ref="areasOfInterestSlideOver"
        :areas-of-interest="areasOfInterest"
      />

      <vetster-partner-reviews
        ref="partnerReviewsSlideOver"
        :partner="partner"
      />

      <vetster-partner-quote-panel-slide-over
        ref="quotePanel"
        :partner="partner"
        :timezone="timezone"
        :search-params="searchParams"
        @back="handleBack"
        @checkout="checkout"
      />
    </div>
    <vetster-membership-slide-over
      v-if="!isSubscription"
      ref="membershipSlideOver"
      @checkout="navigateToBooking"
      @open-related-fees="handleOpenRelatedFees"
    />
    <vetster-subscription-related-fees-slide-over ref="relatedFeesSlideOver" />
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faFileCertificate,
  faHeart,
  faUserMagnifyingGlass,
} from '@fortawesome/pro-light-svg-icons';
import get from 'lodash/get';
import size from 'lodash/size';
import take from 'lodash/take';
import { DateTime } from 'luxon';
import { getCurrentInstance } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';

import VetsterMembershipSlideOver from '@/brand-components/GuidedSearch/VetsterMembershipSlideOver';
import VetsterPartnerAbout from '@/brand-components/GuidedSearch/VetsterPartnerAbout';
import VetsterPartnerAreasOfInterest from '@/brand-components/GuidedSearch/VetsterPartnerAreasOfInterest';
import VetsterPartnerDetailsAbout from '@/brand-components/GuidedSearch/VetsterPartnerDetailsAbout';
import VetsterPartnerDetailsAreasOfInterest from '@/brand-components/GuidedSearch/VetsterPartnerDetailsAreasOfInterest';
import VetsterPartnerDetailsReviews from '@/brand-components/GuidedSearch/VetsterPartnerDetailsReviews';
import VetsterPartnerDetailsSkeleton from '@/brand-components/GuidedSearch/VetsterPartnerDetailsSkeleton';
import VetsterPartnerQuotePanel from '@/brand-components/GuidedSearch/VetsterPartnerQuotePanel';
import VetsterPartnerQuotePanelSlideOver from '@/brand-components/GuidedSearch/VetsterPartnerQuotePanelSlideOver';
import VetsterPartnerReviews from '@/brand-components/GuidedSearch/VetsterPartnerReviews';
import VetsterPartnerTimeslots from '@/brand-components/GuidedSearch/VetsterPartnerTimeslots';
import VetsterQuoteTotal from '@/brand-components/GuidedSearch/VetsterQuoteTotal';
import VetsterQuoteTotalBanner from '@/brand-components/GuidedSearch/VetsterQuoteTotalBanner';
import VetsterRecommendVetStats from '@/brand-components/GuidedSearch/VetsterRecommendVetStats';
import VetsterSubscriptionRelatedFeesSlideOver from '@/brand-components/GuidedSearch/VetsterSubscriptionRelatedFeesSlideOver';
import VetsterAreasOfInterest from '@/brand-components/VetsterAreasOfInterest';
import VetsterOutlineBadge from '@/brand-components/VetsterOutlineBadge';
import VetsterPartnerBadges from '@/brand-components/VetsterPartnerBadges';
import VetsterPartnerDetailCard from '@/brand-components/VetsterPartnerDetailCard';
import VetsterPartnerPhoto from '@/brand-components/VetsterPartnerPhoto';
import VetsterPartnerProfile from '@/brand-components/VetsterPartnerProfile';
import VetsterReviews from '@/brand-components/VetsterReviews';
import VetsterReviewsCount from '@/brand-components/VetsterReviewsCount';
import VetsterSlideNavigationWrapper from '@/brand-components/VetsterSlideNavigationWrapper';
import VetsterSlideOver from '@/brand-components/VetsterSlideOver';
import VetsterSlideOverCommonHeading from '@/brand-components/VetsterSlideOverCommonHeading';
import VetsterSlotWrapper from '@/brand-components/VetsterSlotWrapper';
import VetsterSpeciesTreated from '@/brand-components/VetsterSpeciesTreated';
import useCheckoutHelpers from '@/composables/use-checkout-helpers';
import usePartnerHelpers from '@/composables/use-partner-helpers';
import useSmartTime from '@/composables/use-smart-time';
import Designation from '@/enums/Designation';
import PartnerDetailsSection from '@/enums/PartnerDetailsSection';
import { setCookie } from '@/helpers/cookies';
import { isToday } from '@/helpers/dateUtils';
import parsedLocalStorage from '@/helpers/parsedLocalStorage';
import { getAppUrl } from '@/helpers/url';
import HasConversionRate from '@/mixins/HasConversionRate';
import LocalizesRoutes from '@/mixins/LocalizesRoutes';
import ParseErrors from '@/mixins/ParseErrors';
import PetIcons from '@/mixins/PetIcons';

library.add({
  faFileCertificate,
  faHeart,
  faUserMagnifyingGlass,
});

export default {
  name: 'VetsterPartnerDetails',

  components: {
    VetsterAreasOfInterest,
    VetsterMembershipSlideOver,
    VetsterOutlineBadge,
    VetsterPartnerAbout,
    VetsterPartnerAreasOfInterest,
    VetsterPartnerBadges,
    VetsterPartnerDetailCard,
    VetsterPartnerDetailsAbout,
    VetsterPartnerDetailsAreasOfInterest,
    VetsterPartnerDetailsReviews,
    VetsterPartnerDetailsSkeleton,
    VetsterPartnerPhoto,
    VetsterPartnerProfile,
    VetsterPartnerQuotePanel,
    VetsterPartnerQuotePanelSlideOver,
    VetsterPartnerReviews,
    VetsterPartnerTimeslots,
    VetsterQuoteTotal,
    VetsterQuoteTotalBanner,
    VetsterRecommendVetStats,
    VetsterReviews,
    VetsterReviewsCount,
    VetsterSlideNavigationWrapper,
    VetsterSlideOver,
    VetsterSlideOverCommonHeading,
    VetsterSlotWrapper,
    VetsterSpeciesTreated,
    VetsterSubscriptionRelatedFeesSlideOver,
  },

  mixins: [
    HasConversionRate,
    LocalizesRoutes,
    ParseErrors,
    PetIcons,
  ],

  props: {
    isRecommended: {
      type: Boolean,
      default: false,
    },
    result: {
      type: Object,
      required: true,
    },
    searchParams: {
      type: Object,
      default: () => ({}),
    },
    showRecommendVetCard: {
      type: Boolean,
      default: false,
    },
    timeslotSelected: {
      type: Object,
      default: null,
    },
    timezone: {
      type: String,
      default: undefined,
    },
  },

  setup(props) {
    const root = getCurrentInstance().proxy.$root;
    const { fullName } = usePartnerHelpers(props.result.feature);
    const {
      isSubscription,
      showPaymentOption,
    } = useCheckoutHelpers(root);
    const { validDateFrom } = useSmartTime();

    return {
      fullName,
      isSubscription,
      showPaymentOption,
      validDateFrom,
    };
  },

  data() {
    return {
      areasOfInterestLimit: 4,
      reviews: [],
      reviewsLimit: 10,
      descriptionLengthLimit: 200,
      partnerDetails: {},
      partnerDetailsComponent: null,
      partnerTimeslots: [],
      previousTimeslot: undefined,
      url: null,
    };
  },

  computed: {
    ...mapState('quote', ['pageReady']),
    ...mapGetters('search', {
      getSearchInputs: 'getInputs',
      isVcprSearch: 'isVcprSearch',
    }),
    ...mapGetters('authModal', ['willAuthPromise']),
    ...mapGetters([
      'hasUser',
      'getSiteLocale',
    ]),
    ...mapGetters('subscriptionPlans', ['hasYearlySubscriptionPlan']),
    ...mapGetters('quote', [
      'canGetAvailability',
      'getAvailability',
      'getInputs',
      'getQuote',
      'hasAvailability',
      'hasQuote',
      'isPartnerLoading',
    ]),
    areasOfInterest() {
      return this.partnerDetails?.areas_of_interest;
    },
    canPrescribe() {
      return this.isVcprEnabled && !this.isGeoRestricted;
    },
    descriptionExceedsLimit() {
      return size(this.partner.description) > this.descriptionLengthLimit;
    },
    getFirstAvailableAppointment() {
      return this.partner?.appointments[0];
    },
    getPartnerDetailCards() {
      let cards = [
        'px-details',
        'booked-before',
        'reviews',
      ];

      if (this.isLargeScreen) {
        cards = [
          'book',
          'booked-before',
          'px-details',
          'licenses',
          'species-treated',
          'areas-of-interest',
          'reviews',
        ];
      } else if (this.isRecommended) {
        cards.unshift('book');
        cards.splice(cards.indexOf('booked-before'), 1);
      } else {
        cards.push('book');
      }

      if (this.isRecommended && this.showRecommendVetCard) {
        cards.push('recommend-a-vet');
      }

      return cards;
    },
    getTopRatedText() {
      return `${this.$t('search.top-rated-for-treating')} ${this.$t(`pets.species-plurals.${this.searchSpecies}`).toLowerCase()}`;
    },
    hasVCPR() {
      return this.canPrescribe && this.isVet;
    },
    hasAreasOfInterest() {
      return size(this.areasOfInterest) > 0;
    },
    hasRatings() {
      return this.partner.rating_count > 0;
    },
    hasSpeciesTreated() {
      return size(this.speciesTreated) > 0;
    },
    isLargeScreen() {
      // Note: Adding as a computed property so we can attach a watcher to it
      return this.$screen('lg');
    },
    isGeoRestricted() {
      return this.isVet
        && this.getSiteLocale !== 'en-GB'
        && !this.isInState;
    },
    isInState() {
      return this.result.is_in_state;
    },
    isVcprEnabled() {
      return this.result.is_vcpr_enabled;
    },
    isVet() {
      return this.partner.designation === Designation.Veterinarian;
    },
    lastSeenByVet() {
      return this.partnerDetails?.last_seen_at;
    },
    lastSeenAtDate() {
      const date = DateTime.fromISO(this.lastSeenByVet, {
        zone: this.timezone,
      });
      if (isToday(date)) {
        return this.$t('search.today');
      }

      return `${date.toFormat('LLLL')} ${date.toFormat('d')}`;
    },
    partner() {
      return this.result.feature;
    },
    partnerUrl() {
      return this.localizeUrl(`p/${this.partner.uri_slug}`);
    },
    searchSpecies() {
      return this.getSearchInputs.species_treated[0] ?? null;
    },
    selectedComponent() {
      switch (this.partnerDetailsComponent) {
        case PartnerDetailsSection.AreasOfInterest:
          return VetsterPartnerDetailsAreasOfInterest;
        case PartnerDetailsSection.About:
          return VetsterPartnerDetailsAbout;
        case PartnerDetailsSection.Reviews:
          return VetsterPartnerDetailsReviews;
        default:
          return null;
      }
    },
    selectedComponentBindings() {
      const bindings = {};

      if (this.partnerDetailsComponent === PartnerDetailsSection.AreasOfInterest) {
        bindings.areasOfInterest = this.areasOfInterest;
      } else if (this.partnerDetailsComponent === PartnerDetailsSection.About) {
        bindings.description = this.partner.description;
      } else if (this.partnerDetailsComponent === PartnerDetailsSection.Reviews) {
        bindings.partner = this.partner;
      }

      return bindings;
    },
    speciesTreated() {
      return this.partnerDetails?.species_treated;
    },
    showAllReviewsButton() {
      return this.partner.rating_count > this.reviewsLimit;
    },
    showAreasOfInterestButton() {
      return size(this.areasOfInterest) > this.areasOfInterestLimit;
    },
  },

  watch: {
    isLargeScreen: {
      async handler(newValue, oldValue) {
        if (newValue !== oldValue && oldValue !== undefined) {
          await this.updatePartnerAndFetchTimeslots();
        }
        this.updateReviewsDisplayLimit(newValue);
        this.$refs?.membershipSlideOver?.close();
      },
      immediate: true,
    },
    partner: {
      async handler() {
        await this.setupPartnerData();
      },
      deep: true,
    },
    result: {
      handler(newVal) {
        this.$store.commit('search/setInputs', { search_result_uuid: newVal.search_result_uuid });
      },
      deep: true,
      immediate: true,
    },
    selectedComponent: {
      handler(newValue) {
        if (newValue) {
          this.$refs.scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      },
    },
  },

  async mounted() {
    this.$store.commit('quote/setPageReady', false);
    await this.fetchVcprLocation();
    this.$store.commit('search/setInputs', { search_result_uuid: this.result.search_result_uuid });
    await this.setupPartnerData();
    this.partnerTimeslots = take(this.getAvailability, 4);
    this.$store.commit('quote/setPageReady', true);

    this.$analytics.track({
      eventName: 'continue_booking_load',
      parent: 'guided-search',
      child: 'select-booking',
      version: '2.0',
    });
  },

  methods: {
    ...mapActions('authModal', [
      'setAuthModalMode',
      'setIsBooking',
    ]),
    ...mapActions('quote', ['fetchVcprLocation']),
    async checkout() {
      if (this.hasYearlySubscriptionPlan && !this.isSubscription && this.showPaymentOption) {
        this.openMembershipSlideOver();

        return;
      }

      await this.navigateToBooking();
    },
    close() {
      this.$emit('close');
      this.$refs.partnerSlideOver?.close();
      this.$store.commit('quote/setAvailability', []);
      window.removeEventListener('popstate', this.closeOnHistoryBack);
    },
    closeOnHistoryBack(event) {
      if (!event.state?.isPartnerDetails) {
        this.close();

        // Erase forward history since there is no frontend route handling for restoring
        // this.partnerUrl with this modal open.
        window.history.pushState(null, document.title, window.location);
      }
    },
    closePartnerDetails(event) {
      event?.stopPropagation();
      this.partnerDetailsComponent = null;
      window.history.replaceState(null, null, this.url);
    },
    async fetchPartnerDetails() {
      try {
        const response = await this.$api.get(`p/partner/${this.partner.uuid}`);
        this.partnerDetails = response.data.data;
      } catch (e) {
        this.showError(e, true);
      }
    },
    async fetchReviews() {
      try {
        const response = await this.$api.get(`p/partner/${this.partner.uuid}/reviews`, {
          params: {
            page: 1,
            perPage: this.reviewsLimit,
          },
        });
        this.reviews = response.data.data;
      } catch (e) {
        this.showError(e, true);
      }
    },
    getBookingUrl(selectedMembership) {
      const { uuid } = this.getQuote;

      if (this.isSubscription) {
        return getAppUrl(`/cx/booking/${uuid}`);
      }

      return getAppUrl(`/cx/booking/${uuid}`, {
        'payment-option': selectedMembership ? 'membership' : 'appointment',
      });
    },
    getDescription(description) {
      if (this.descriptionExceedsLimit) {
        return `${description.substring(0, this.descriptionLengthLimit)}...`;
      }

      return description;
    },
    handleBack() {
      this.previousTimeslot = this.getInputs.timeslot;
    },
    handleOpenRelatedFees() {
      this.$refs.relatedFeesSlideOver?.open();
    },
    handleScroll(e) {
      if (this.partnerDetailsComponent === PartnerDetailsSection.Reviews) {
        this.$refs?.[this.selectedComponent.name]?.$refs?.reviewList?.fetchReviewsNextPage?.(e);
      }
    },
    async navigateToBooking(selectedMembership = false) {
      if (this.hasQuote) {
        if (!this.hasUser && !(await this.registerNewAccount())) {
          return;
        }

        this.$analytics.track({
          eventName: 'continue_booking_submit',
          parent: 'guided-search',
          child: 'select-booking',
          event_status: 'SUCCESS',
          version: '2.0',
        });

        window.history.replaceState(null, null, this.url);

        setTimeout(() => {
          window.location.href = this.getBookingUrl(selectedMembership);
        }, 300);
      }
    },
    openAboutSlideOver() {
      if (this.isLargeScreen) {
        this.partnerDetailsComponent = PartnerDetailsSection.About;
      } else {
        this.$refs.aboutSlideOver.open();
      }
    },
    openAreasOfInterestSlideOver() {
      if (this.isLargeScreen) {
        this.partnerDetailsComponent = PartnerDetailsSection.AreasOfInterest;
      } else {
        this.$refs.areasOfInterestSlideOver.open();
      }
    },
    open() {
      this.$refs.partnerSlideOver.open();
      this.setPartnerUrl();
    },
    openMembershipSlideOver() {
      this.$refs.membershipSlideOver.open();
    },
    registerNewAccount() {
      this.setAuthModalMode('register');
      this.setIsBooking(true);

      return this.willAuthPromise;
    },
    rememberPartner() {
      const url = new URL(window.location.href);
      url.searchParams.set('search_result_uuid', this.result.search_result_uuid);
      setCookie('v-search-url', url.toString(), '1h');
    },
    async setupPartnerData() {
      this.$store.commit('quote/setIsPartnerLoading', true);
      await this.fetchReviews();
      await this.fetchPartnerDetails();
      await this.updatePartnerAndFetchTimeslots();
      this.$store.commit('quote/setIsPartnerLoading', false);
    },
    toggleReviews() {
      if (this.isLargeScreen) {
        this.partnerDetailsComponent = PartnerDetailsSection.Reviews;
      } else {
        this.$refs.partnerReviewsSlideOver.open();
      }
    },
    toggleQuotePanel() {
      this.$analytics.track({
        eventName: 'show_more_dates',
        parent: 'guided-search',
        child: 'select-booking',
        version: '2.0',
      });
      this.$refs.quotePanel.openSlideover();
    },
    async updatePartnerAndFetchTimeslots() {
      const timezone = this.timezone
        ? this.timezone
        : DateTime.local().zoneName;

      this.$store.commit('quote/setTimezone', timezone);

      this.$store.commit('quote/setPartner', this.partner);

      this.$store.commit(
        'quote/setInputsDate',
        this.validDateFrom(this.getFirstAvailableAppointment?.start_at
          || get(parsedLocalStorage('quoteParams'), 'date', '')),
      );

      if (this.timeslotSelected) {
        this.$store.commit('quote/setInputsTimeslot', this.timeslotSelected);
      } else {
        this.$store.commit(
          'quote/setInputsTimeslot',
          size(this.getFirstAvailableAppointment)
            ? this.getFirstAvailableAppointment
            : get(parsedLocalStorage('quoteParams'), 'timeslot', ''),
        );
      }

      this.$store.commit('quote/setInputsTimezone', timezone);

      if (this.getInputs.date) {
        await this.$store.dispatch('quote/setDateOnQuote');
      }

      if (this.canGetAvailability) {
        this.$store.commit('quote/setAvailabilityFilters', {
          range: { weeks: 2 },
          limit: 4,
        });
        await this.$store.dispatch('quote/fetchAvailabilityAndUseExistingTimeslot');
        this.$store.commit('quote/resetAvailabilityFilters');
      }
    },
    updateReviewsDisplayLimit(isLargeScreen) {
      if (isLargeScreen) {
        this.areasOfInterestLimit = 6;
        this.reviewsLimit = 4;
      } else {
        this.areasOfInterestLimit = 4;
        this.reviewsLimit = 10;
      }
    },
    setPartnerUrl() {
      this.rememberPartner();
      this.url = window.location.href;

      window.history.pushState(
        { isPartnerDetails: true },
        null,
        this.partnerUrl,
      );

      window.addEventListener('popstate', this.closeOnHistoryBack);
    },
  },
};
</script>
